// Presentations list (W10) — other Business UI handoff, 2026-10-07.
//
// Cross-project staff listing of every presentation: page header, a metric
// strip (what is waiting on the client, what is waiting on us), status tabs,
// search, and a design table. Each row opens the presentation inside its
// project (same tab — house standard).
//
// No create-new CTA here. Reps start a presentation from inside a project,
// since every presentation must be tied to one.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { PageHeader } from "@/components/ui/PageHeader";
import { StatusPill } from "@/components/ui/StatusPill";
import { api } from "@/lib/api";
import { cn, timeAgo } from "@/lib/utils";

type PresentationStatus =
  | "draft"
  | "open"
  | "sent"
  | "viewed"
  | "comments_pending"
  | "approved"
  | "rejected";

interface PresentationListRow {
  id: string;
  presentationNumber: number;
  status: PresentationStatus;
  projectId: string;
  projectName: string;
  projectNumber: number;
  clientId: string;
  clientName: string;
  introduction: string | null;
  publicShareUuid: string;
  sentAt: string | null;
  firstViewedAt: string | null;
  lastViewedAt: string | null;
  viewCount: number | null;
  itemCount: number;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  rows: PresentationListRow[];
  nextCursor: { createdAt: string; id: string } | null;
}

type StatusFilter = PresentationStatus | "all";

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "viewed", label: "Viewed" },
  { value: "comments_pending", label: "Client notes" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const STATUS_TONES: Record<
  PresentationStatus,
  "active" | "prospect" | "dormant" | "closed" | "warn" | "info"
> = {
  draft: "info",
  open: "info",
  sent: "prospect",
  viewed: "prospect",
  comments_pending: "warn",
  approved: "active",
  rejected: "closed",
};

const STATUS_LABELS: Record<PresentationStatus, string> = {
  draft: "Draft",
  open: "Open",
  sent: "Sent",
  viewed: "Viewed",
  comments_pending: "Client notes",
  approved: "Approved",
  rejected: "Rejected",
};

export function PresentationsListPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (statusFilter !== "all") params.set("status", statusFilter);
    params.set("limit", "100");
    return params.toString();
  }, [search, statusFilter]);

  const listQ = useInfiniteQuery({
    queryKey: ["presentations", "list", queryString],
    initialPageParam: null as ListResponse["nextCursor"],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams(queryString);
      if (pageParam) {
        params.set("cursor_created_at", pageParam.createdAt);
        params.set("cursor_id", pageParam.id);
      }
      return api<ListResponse>(`/presentations?${params.toString()}`);
    },
    getNextPageParam: (last) => last.nextCursor,
  });

  // The metric strip counts the latest 100 presentations regardless of the
  // filters below it, so the numbers don't jump while the rep searches.
  const summaryQ = useQuery({
    queryKey: ["presentations", "list", "summary"],
    queryFn: () => api<ListResponse>(`/presentations?limit=100`),
  });

  const rows = useMemo(
    () => listQ.data?.pages.flatMap((p) => p.rows) ?? [],
    [listQ.data],
  );
  const isLoading = listQ.isLoading;
  const isError = listQ.isError;
  const isEmptyOverall =
    !isLoading && !isError && rows.length === 0 && !search && statusFilter === "all";

  return (
    <div className="mx-auto w-full">
      <PageHeader
        eyebrow="Sales / Presentations"
        title="Presentations"
        intro="Products picked for a client, shared by private link. Open one to add products, set prices, preview and send."
      />

      <MetricStrip
        rows={summaryQ.data?.rows ?? null}
        capped={Boolean(summaryQ.data?.nextCursor)}
        isError={summaryQ.isError}
        onPick={setStatusFilter}
      />

      {/* Status tabs + search */}
      <div className="mb-4 mt-7 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-rule">
        <div className="-mb-px flex flex-wrap gap-x-5" role="tablist" aria-label="Filter by status">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              role="tab"
              aria-selected={statusFilter === f.value}
              onClick={() => setStatusFilter(f.value)}
              className={cn(
                "min-h-[40px] border-b-2 px-0.5 text-sm transition-colors",
                statusFilter === f.value
                  ? "border-ink font-medium text-ink"
                  : "border-transparent text-muted hover:text-ink",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative mb-2 w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden />
          <Input
            placeholder="Search project or client"
            aria-label="Search presentations by project or client"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {isEmptyOverall ? (
        <div className="rounded-md border border-rule bg-white px-5 py-12 text-center">
          <p className="font-medium text-ink">No presentations yet.</p>
          <p className="mt-1 text-sm text-muted">
            Open a project and choose New presentation to pick products for a client.
          </p>
          <Link
            to="/projects"
            className="mt-4 inline-flex min-h-[40px] items-center rounded border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas"
          >
            View projects
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-rule bg-white" role="region" aria-label="Presentations" tabIndex={0}>
          <table className="w-full whitespace-nowrap text-left">
            <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-[11px] font-medium text-muted">
              <tr>
                <th scope="col" className="px-[18px] py-[15px] font-medium">Presentation</th>
                <th scope="col" className="px-[18px] py-[15px] font-medium">Project / client</th>
                <th scope="col" className="px-[18px] py-[15px] font-medium">Status</th>
                <th scope="col" className="px-[18px] py-[15px] text-right font-medium">Products</th>
                <th scope="col" className="px-[18px] py-[15px] font-medium">Last activity</th>
                <th scope="col" className="px-[18px] py-[15px] font-medium">Rep</th>
              </tr>
            </thead>
            <tbody className="text-[13px]">
              {isLoading ? (
                [0, 1, 2, 3].map((i) => (
                  <tr key={i} className="border-b border-[#e7e7e4] last:border-0">
                    <td colSpan={6} className="px-[18px] py-[15px]">
                      <div className="h-4 w-full animate-pulse rounded bg-canvas" />
                    </td>
                  </tr>
                ))
              ) : isError ? (
                <tr>
                  <td colSpan={6} className="px-[18px] py-[15px] text-risk" role="alert">
                    We couldn't load presentations. Nothing has changed — refresh to try again.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-[18px] py-8 text-center text-muted">
                    No presentations match. Clear the search or choose All.
                  </td>
                </tr>
              ) : (
                rows.map((row) => <PresentationRow key={row.id} row={row} />)
              )}
            </tbody>
          </table>
        </div>
      )}
      {listQ.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => void listQ.fetchNextPage()}
            disabled={listQ.isFetchingNextPage}
            className="inline-flex min-h-[40px] items-center rounded border border-rule bg-white px-4 text-sm text-ink hover:bg-canvas disabled:opacity-50"
          >
            {listQ.isFetchingNextPage ? "Loading…" : "Show more"}
          </button>
        </div>
      )}
    </div>
  );
}

// Metric strip (handoff): white box, fine rules between cells. Each number is
// a shortcut to that status tab.
function MetricStrip(props: {
  rows: PresentationListRow[] | null;
  capped: boolean;
  isError: boolean;
  onPick: (s: StatusFilter) => void;
}) {
  const { rows, capped, isError, onPick } = props;
  if (isError) return null;
  const count = (...s: PresentationStatus[]): number =>
    (rows ?? []).filter((r) => s.includes(r.status)).length;
  const cells: Array<{ label: string; value: number; note: string; filter: StatusFilter }> = [
    { label: "Client notes to read", value: count("comments_pending"), note: "Clients sent feedback", filter: "comments_pending" },
    { label: "Opened by the client", value: count("viewed"), note: "Viewed, no notes yet", filter: "viewed" },
    { label: "Sent, not opened", value: count("sent"), note: "Worth a nudge", filter: "sent" },
    { label: "Drafts", value: count("draft"), note: "Not sent yet", filter: "draft" },
  ];
  return (
    <section aria-label="Presentation summary">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-rule bg-rule lg:grid-cols-4">
        {cells.map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={() => onPick(c.filter)}
            className="flex flex-col bg-white p-5 text-left transition-colors hover:bg-[#fafaf8]"
          >
            <span className="text-xs text-muted">{c.label}</span>
            {rows === null ? (
              <span className="mt-2 h-7 w-12 animate-pulse rounded bg-canvas" aria-hidden />
            ) : (
              <span className="mt-1.5 text-[29px] font-medium leading-tight tracking-[-0.8px] text-ink tabular-nums">
                {c.value.toLocaleString()}
              </span>
            )}
            <span className="mt-1.5 text-xs text-muted">{c.note}</span>
          </button>
        ))}
      </div>
      {capped && (
        <p className="mt-2 text-xs text-muted">Counts cover the latest 100 presentations.</p>
      )}
    </section>
  );
}

function PresentationRow({ row }: { row: PresentationListRow }) {
  const activityLabel = row.lastViewedAt
    ? `Viewed ${timeAgo(row.lastViewedAt)}`
    : row.sentAt
      ? `Sent ${timeAgo(row.sentAt)}`
      : `Created ${timeAgo(row.createdAt)}`;
  return (
    <tr className="border-b border-[#e7e7e4] last:border-0 hover:bg-[#fafaf8]">
      <td className="px-[18px] py-[15px]">
        <Link to={`/projects/${row.projectId}/presentation/${row.id}`} className="font-medium text-ink hover:underline">
          P-{row.presentationNumber}
        </Link>
      </td>
      <td className="px-[18px] py-[15px]">
        <Link to={`/projects/${row.projectId}/presentation/${row.id}`} className="font-medium text-ink hover:underline">
          {row.projectName}
        </Link>
        <span className="block text-[11px] text-muted">
          {row.clientName} · #{row.projectNumber}
        </span>
      </td>
      <td className="px-[18px] py-[15px]">
        <StatusPill tone={STATUS_TONES[row.status]}>{STATUS_LABELS[row.status]}</StatusPill>
      </td>
      <td className="px-[18px] py-[15px] text-right tabular-nums">
        {row.itemCount.toLocaleString()}
      </td>
      <td className="px-[18px] py-[15px] text-muted">
        {activityLabel}
        {row.viewCount && row.viewCount > 0 ? (
          <span className="block text-[11px]">
            {row.viewCount.toLocaleString()} view{row.viewCount === 1 ? "" : "s"}
          </span>
        ) : null}
      </td>
      <td className="px-[18px] py-[15px] text-muted">{row.createdByName ?? "—"}</td>
    </tr>
  );
}
